import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getSemana, formatRangoSemana } from "../../../common/utils/semana";
import { fetchSucursales } from "../../../../RRHH2/features/legajos/services/empleados.api";
import { useUsuariosSinPedir } from "../hooks/useUsuariosSinPedir";
import SinPedirTable from "../components/SinPedirTable";

const semanaActual = getSemana(0);

const FILTROS_INICIALES = {
  desde: semanaActual.desde,
  hasta: semanaActual.hasta,
  id_sucursal: "",
};

const SinPedirPage = () => {
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [sucursales, setSucursales] = useState([]);

  useEffect(() => {
    fetchSucursales()
      .then((res) => setSucursales(res || []))
      .catch(() => setSucursales([]));
  }, []);

  const filtrosQuery = useMemo(
    () => ({
      desde: filtros.desde || undefined,
      hasta: filtros.hasta || undefined,
      id_sucursal: filtros.id_sucursal || undefined,
    }),
    [filtros],
  );

  const { data, loading, error, rangoValido } = useUsuariosSinPedir(filtrosQuery);

  const handleFieldChange = (field, value) =>
    setFiltros((prev) => ({ ...prev, [field]: value }));

  const handleLimpiar = () => setFiltros(FILTROS_INICIALES);

  const sinResultados = rangoValido && !loading && !error && data.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 3,
          border: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          type="date"
          label="Desde"
          InputLabelProps={{ shrink: true }}
          value={filtros.desde}
          onChange={(e) => handleFieldChange("desde", e.target.value)}
        />
        <TextField
          size="small"
          type="date"
          label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={filtros.hasta}
          onChange={(e) => handleFieldChange("hasta", e.target.value)}
        />

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <Select
            displayEmpty
            value={filtros.id_sucursal}
            onChange={(e) => handleFieldChange("id_sucursal", e.target.value)}
          >
            <MenuItem value="">
              <em>Todas las sucursales</em>
            </MenuItem>
            {sucursales.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          size="small"
          color="error"
          startIcon={<CloseIcon />}
          onClick={handleLimpiar}
          sx={{ textTransform: "none" }}
        >
          Limpiar
        </Button>
      </Paper>

      {!rangoValido && (
        <Alert severity="info">
          Elegí un rango de fechas válido (la fecha "hasta" no puede ser anterior
          a "desde").
        </Alert>
      )}

      {error && (
        <Alert severity="error">
          {error.message || "No se pudo obtener el listado. Intentá de nuevo."}
        </Alert>
      )}

      {rangoValido && !error && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? "Buscando usuarios..."
              : `${data.length} ${
                  data.length === 1 ? "usuario no pidió" : "usuarios no pidieron"
                } vianda entre ${formatRangoSemana(filtros.desde, filtros.hasta)}`}
          </Typography>
          {!loading && (
            <Chip
              label={data.length}
              size="small"
              color={data.length ? "warning" : "success"}
            />
          )}
        </Box>
      )}

      {sinResultados ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 5,
            color: "text.secondary",
          }}
        >
          <CheckCircleIcon
            sx={{ fontSize: 32, color: "success.main", opacity: 0.7 }}
          />
          <Typography>
            Todos los usuarios pidieron su vianda en este rango.
          </Typography>
        </Box>
      ) : (
        rangoValido && !error && <SinPedirTable data={data} loading={loading} />
      )}
    </Box>
  );
};

export default SinPedirPage;
